/* eslint-disable react/jsx-props-no-spreading */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import { InitInfoData } from '../utils/initInfoUtils';
import AppContext from './AppContext';
import { USER_STATES } from '../utils/constants';

interface InitInfoContextType {
  initInfoData: InitInfoData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface InitInfoProviderProps {
  children: ReactNode;
}

const InitInfoContext = createContext<InitInfoContextType | undefined>(undefined);

export const InitInfoProvider: React.FC<InitInfoProviderProps> = ({ children }) => {
  const [initInfoData, setInitInfoData] = useState<InitInfoData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useContext(AppContext);

  const loadInitInfo = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.getMerchantOpportunitiesInitInfo();

      if (response && response.success) {
        setInitInfoData(response.data);
      } else {
        const errorMessage = 'Failed to load init info data';
        setError(errorMessage);
        console.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Error loading init info data';
      setError(errorMessage);
      console.error(errorMessage, error);
    } finally {
      setLoading(false);
    }
  };

  const refetch = async (): Promise<void> => {
    await loadInitInfo();
  };

  useEffect(() => {
    // Only load init info when user is authenticated (not false, not undefined)
    if (user && user !== USER_STATES.NOT_AUTHENTICATED && user !== USER_STATES.AUTHENTICATING) {
      loadInitInfo();
    } else {
      // Set loading to false when user is not authenticated to avoid infinite loading
      setLoading(false);
    }
  }, [user]);

  const value: InitInfoContextType = {
    initInfoData,
    loading,
    error,
    refetch,
  };

  return <InitInfoContext.Provider value={value}>{children}</InitInfoContext.Provider>;
};

export const useInitInfo = (): InitInfoContextType => {
  const context = useContext(InitInfoContext);
  if (context === undefined) {
    throw new Error('useInitInfo must be used within an InitInfoProvider');
  }
  return context;
};

export default InitInfoContext;
